import { ActionButton } from '@/components/common/ActionButton';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import { MOTION } from '@/components/common/motion';
import { TableSkeleton } from '@/components/common/Skeleton';
import { CARD_PADDED, ICON_TILE, PANEL_TITLE, SECTION_LABEL } from '@/components/common/surfaces';
import { ImagePreviewDialog } from '@/components/dialogs/ImagePreviewDialog';
import { TextViewDialog } from '@/components/dialogs/TextViewDialog';
import { aptGenerationApi } from '@/api/apt_generation.api';
import { detectionsApi } from '@/api/detections.api';
import { useDetections } from '@/hooks/useDetections';
import { useHoles } from '@/hooks/useHoles';
import type { HoleObjectModel } from '@/types/apt_generation.types';
import { ORIENTATION_ORDER } from '@/types/detection.types';
import type { ProcedureModel } from '@/types/procedure.types';
import {
  TABLE_CARD,
  TABLE_CELL,
  TABLE_CELL_NUM,
  TABLE_FOOTER,
  TABLE_HEADER_CELL,
  TABLE_HEADER_ROW,
  TABLE_ROW,
  TABLE_ROW_BG_ODD,
} from '@/utils/stickyColumns';
import {
  CaretDown,
  CaretLeft,
  CaretRight,
  Check,
  Circle,
  DotsSixVertical,
  DownloadSimple,
  FileText,
  ListBullets,
  PencilSimple,
  Target,
  WarningCircle,
  X,
} from '@phosphor-icons/react';
import { TabPanel, TabView } from 'primereact/tabview';
import type { FC } from 'react';
import { Fragment, useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

/**
 * The orientation tab strip, expressed as passthrough classes on PrimeReact's `TabView`.
 *
 * Every utility carries `!`. The bootstrap theme and the app's own PrimeReact overrides are
 * unlayered stylesheets, and in the cascade an unlayered `!important` declaration ranks *below*
 * a layered one — so the important variant is what lets a utility from Tailwind's `utilities`
 * layer win, whatever specificity the vendor selector happens to have.
 */
const TAB_STRIP_WRAPPER = [
  // The `ul`: one row of tabs over a single hairline. The theme draws its own bottom border and
  // `index.css` adds a second, darker one through `::after`; both are switched off here.
  '[&_.p-tabview-nav]:gap-1!',
  '[&_.p-tabview-nav]:border-0!',
  '[&_.p-tabview-nav]:border-b!',
  '[&_.p-tabview-nav]:border-gray-200!',
  '[&_.p-tabview-nav]:bg-transparent!',
  '[&_.p-tabview-nav]:after:hidden!',
  // A tab keeps its width and the strip scrolls, rather than six tabs squashing on a phone.
  '[&_.p-tabview-header]:shrink-0!',
  // The tab itself.
  '[&_.p-tabview-nav-link]:h-auto!',
  '[&_.p-tabview-nav-link]:rounded-t-lg!',
  '[&_.p-tabview-nav-link]:rounded-b-none!',
  '[&_.p-tabview-nav-link]:border-0!',
  '[&_.p-tabview-nav-link]:bg-transparent!',
  '[&_.p-tabview-nav-link]:px-3!',
  '[&_.p-tabview-nav-link]:py-2!',
  '[&_.p-tabview-nav-link]:text-sm!',
  '[&_.p-tabview-nav-link]:font-medium!',
  '[&_.p-tabview-nav-link]:text-gray-500!',
  '[&_.p-tabview-nav-link]:transition-all!',
  '[&_.p-tabview-nav-link]:duration-300!',
  '[&_.p-tabview-nav-link]:ease-[cubic-bezier(0.32,0.72,0,1)]!',
  '[&_.p-tabview-nav-link]:motion-reduce:transition-none!',
  '[&_.p-tabview-nav-link]:hover:text-gray-900!',
  '[&_.p-tabview-nav-link]:active:scale-[0.98]!',
  // The theme's focus ring is the bootstrap blue. This is the app accent, drawn inside the tab
  // because the link clips its own overflow.
  '[&_.p-tabview-nav-link]:focus-visible:shadow-[inset_0_0_0_2px_rgba(27,21,49,0.4)]!',
  // The panel under the strip brings only the gap; its content owns the rest.
  '[&_.p-tabview-panels]:bg-transparent!',
  '[&_.p-tabview-panels]:px-0!',
  '[&_.p-tabview-panels]:pb-0!',
  '[&_.p-tabview-panels]:pt-4!',
].join(' ');

/** The active tab: darker, heavier, and the only one carrying the accent bar. */
const TAB_HEADER_ACTIVE = '[&_.p-tabview-nav-link]:font-semibold! [&_.p-tabview-nav-link]:text-gray-900!';

/** Header cell: the shared typography plus the padding and alignment the recipe leaves to the caller. */
const HEADER_CELL = `${TABLE_HEADER_CELL} px-3 py-2 text-left align-bottom`;
const HEADER_CELL_NUM = `${TABLE_HEADER_CELL} px-3 py-2 text-right align-bottom`;

/** The row that names a block of columns, above the column names themselves. */
const GROUP_LABEL_CELL = `${SECTION_LABEL} px-3 py-2 text-left`;

/**
 * A body row without a background of its own.
 *
 * The shared `TABLE_ROW` bakes `bg-white` in, and a second background utility on the same element
 * is settled by stylesheet order rather than by the order of the class string — so every row that
 * carries a tint (an error, an expanded detail row) starts from this instead.
 */
const ROW_BASE = `h-12 border-b border-gray-100 last:border-b-0 ${MOTION}`;

/** An expanded detail row: shorter than a data row, and on the quieter of the two row grays. */
const SUB_ROW = `h-10 border-b border-gray-100 ${TABLE_ROW_BG_ODD} ${MOTION}`;

/** A padding row, holding the table's height steady across pages. */
const EMPTY_ROW = 'h-12 border-b border-gray-100 bg-white last:border-b-0';

/** A cell in an expanded detail row. */
const SUB_CELL = 'px-3 align-middle text-sm text-gray-700';
const SUB_CELL_NUM = `${SUB_CELL} text-right tabular-nums`;

/** The preview chip, at the size the row can carry without growing. */
const PREVIEW_BOX = 'flex h-8 w-12 items-center justify-center overflow-hidden rounded bg-gray-50';

/** One segment of the holes / procedures switch. */
const SEGMENT_BASE = `inline-flex items-center gap-1 rounded px-3 py-1 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 active:scale-[0.98] ${MOTION}`;
const SEGMENT_ACTIVE = 'bg-white text-gray-900 shadow-sm';
const SEGMENT_INACTIVE = 'text-gray-500 hover:text-gray-900';

/** A page number in the table footer. */
const PAGE_BUTTON_BASE = `h-8 min-w-8 rounded px-2 text-sm font-medium tabular-nums focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 active:scale-[0.98] ${MOTION}`;
const PAGE_BUTTON_CURRENT = 'bg-gray-900 text-white';
const PAGE_BUTTON_IDLE = 'text-gray-600 hover:bg-gray-100 hover:text-gray-900';

interface APTGenerationPanelProps {
  projectId: string;
  /** Id of the project's STEP document. When provided, only extractions for it are listed.
   *  The panel lists extractions with extraction_type "holes" only (other types are hidden). */
  modelId?: string | null;
  /** Called when APT Generation panel is past loading (task completed); false while loading. */
  onAptGenerationReady?: (ready: boolean) => void;
}

const getRegenerateStorageKey = (projectId: string) => `regenerateTask_${projectId}_target`;

/**
 * Group procedures by (procedure_name, tool_number, tool_diameter, total_depth, mo_type, spindle_speed_rpm)
 * so that only procedures that share all these attributes are merged. Collect associated holes per group.
 */
/** True when full hole list was fetched for this extraction (including empty list). */
function hasLoadedHolesList(holesData: Record<string, HoleObjectModel[]>, extractionId: string): boolean {
  return Object.prototype.hasOwnProperty.call(holesData, extractionId);
}

function buildProcedureGroups(
  holes: HoleObjectModel[]
): { key: string; procedure: ProcedureModel; holes: HoleObjectModel[] }[] {
  const map = new Map<string, { procedure: ProcedureModel; holes: HoleObjectModel[] }>();
  for (const hole of holes) {
    for (const proc of hole.procedures ?? []) {
      const key = [
        proc.procedure_name ?? '',
        proc.assembly.tool?.tool_number_unique ?? '',
        proc.assembly.tool?.tool_diameter ?? '',
        proc.total_depth,
        proc.mo_type,
        proc.spindle_speed_rpm,
      ].join('|');
      const existing = map.get(key);
      if (!existing) {
        map.set(key, { procedure: proc, holes: [hole] });
      } else {
        existing.holes.push(hole);
      }
    }
  }
  return Array.from(map.entries(), ([key, { procedure, holes }]) => ({ key, procedure, holes }));
}

export const APTGenerationPanel: FC<APTGenerationPanelProps> = ({ projectId, modelId, onAptGenerationReady }) => {
  const { t } = useTranslation();
  const {
    detections,
    isLoading: isLoadingDetections,
    error: detectionsError,
    refetch: refetchDetections,
  } = useDetections(projectId);
  const { fetchHoles, isLoading: isLoadingHoles } = useHoles();
  const [expandedHoles, setExpandedHoles] = useState<Set<string>>(new Set());
  const [holesData, setHolesData] = useState<Record<string, HoleObjectModel[]>>({});
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [previewHoleId, setPreviewHoleId] = useState<string | null>(null);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [previewProcedureId, setPreviewProcedureId] = useState<string | null>(null);
  const [previewThumbnails, setPreviewThumbnails] = useState<Record<string, string>>({});
  const [previewProcedureThumbnails, setPreviewProcedureThumbnails] = useState<Record<string, string>>({});
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [showAPTSourceDialog, setShowAPTSourceDialog] = useState(false);
  const [aptSourceContent, setAptSourceContent] = useState<string>('');
  const [aptSourceHoleId, setAptSourceHoleId] = useState<string | null>(null);
  const [aptSourceFilename, setAptSourceFilename] = useState<string | null>(null);
  const [holesCounts, setHolesCounts] = useState<Record<string, number>>({});
  const [currentPage, setCurrentPage] = useState<Record<string, number>>({});
  const [viewMode, setViewMode] = useState<'holes' | 'procedures'>('procedures');
  const [expandedProcedures, setExpandedProcedures] = useState<Set<string>>(new Set());
  const [expandedProcedureHolesPage, setExpandedProcedureHolesPage] = useState<Record<string, number>>({});
  const [isDownloadingZip, setIsDownloadingZip] = useState(false);
  const [editingProcedureId, setEditingProcedureId] = useState<string | null>(null);
  const [editingProcedureName, setEditingProcedureName] = useState('');
  /** Extraction id for which a swap-order request is in progress; disables drag handle. */
  const [swappingExtractionId, setSwappingExtractionId] = useState<string | null>(null);
  const HOLES_PER_PAGE = 6;
  const PROCEDURES_PER_PAGE = 6;
  const EXPANDED_HOLES_PER_PAGE = 6;
  const wasRegeneratingRef = useRef(false);
  const refetchDetectionsRef = useRef(refetchDetections);
  refetchDetectionsRef.current = refetchDetections;

  // Check if regeneration is in progress and handle state changes
  useEffect(() => {
    const storageKey = getRegenerateStorageKey(projectId);
    const checkRegeneration = () => {
      const taskId = localStorage.getItem(storageKey);
      const currentlyRegenerating = !!taskId;

      // If regeneration just started, clear all holes data and cache
      if (currentlyRegenerating && !wasRegeneratingRef.current) {
        setHolesData({});
        setExpandedHoles(new Set());
        setHolesCounts({});
        setPreviewThumbnails({});
        setPreviewProcedureThumbnails({});

        // Clear cache
        try {
          localStorage.removeItem(`holes_counts_${projectId}`);
          // Clear blob cache for this project
          import('@/utils/blobCache').then(({ clearProjectCache }) => {
            clearProjectCache(projectId).catch(() => {
              // Ignore errors
            });
          });
        } catch (_error) {
          // Ignore
        }
      }

      // If regeneration just finished, refetch detections
      if (!currentlyRegenerating && wasRegeneratingRef.current) {
        refetchDetectionsRef.current();
      }

      setIsRegenerating(currentlyRegenerating);
      wasRegeneratingRef.current = currentlyRegenerating;
    };

    // Check on mount
    const initialTaskId = localStorage.getItem(storageKey);
    wasRegeneratingRef.current = !!initialTaskId;
    setIsRegenerating(!!initialTaskId);
    if (initialTaskId) {
      // If regeneration is already in progress, clear holes data
      setHolesData({});
      setExpandedHoles(new Set());
    }

    // Listen for storage changes (when regeneration starts/ends)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === storageKey) {
        checkRegeneration();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Also poll localStorage periodically (for same-tab changes)
    const interval = setInterval(checkRegeneration, 500);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [projectId]);

  /** Extractions shown in this panel: same model (when modelId set) and APT/holes type only. */
  const aptPanelDetections = useMemo(() => {
    const byModel = modelId ? detections.filter((d) => d.model_id === modelId) : detections;
    return byModel.filter((d) => d.extraction_type === 'holes');
  }, [detections, modelId]);

  const sortedDetections = useMemo(() => {
    return [...aptPanelDetections].sort((a, b) => {
      const aIndex = ORIENTATION_ORDER.indexOf(a.orientation);
      const bIndex = ORIENTATION_ORDER.indexOf(b.orientation);
      if (aIndex === -1 && bIndex === -1) return 0;
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });
  }, [aptPanelDetections]);

  useEffect(() => {
    setActiveTabIndex((idx) => {
      if (sortedDetections.length === 0) return 0;
      return idx >= sortedDetections.length ? 0 : idx;
    });
  }, [sortedDetections]);

  const currentExtractionId = useMemo(() => {
    if (sortedDetections.length > 0 && activeTabIndex >= 0 && activeTabIndex < sortedDetections.length) {
      return sortedDetections[activeTabIndex].id;
    }
    return null;
  }, [sortedDetections, activeTabIndex]);

  // Fetch holes when tab changes or extraction changes
  useEffect(() => {
    if (!currentExtractionId) {
      return;
    }

    // Fetch holes if not already loaded
    if (!holesData[currentExtractionId]) {
      fetchHoles(currentExtractionId).then(async (allHoles) => {
        const holes = allHoles.filter((h) => h.object_type !== 'Chamfer');
        setHolesData((prev) => ({ ...prev, [currentExtractionId]: holes }));

        // Load preview thumbnails for holes and procedures (with cache)
        const { getCachedBlob, setCachedBlob } = await import('@/utils/blobCache');
        const holeThumbnailPromises = holes.map(async (hole) => {
          const cacheKey = `png_${hole.id}`;
          const cachedBlob = await getCachedBlob(cacheKey);
          if (cachedBlob) {
            return { holeId: hole.id, imageUrl: URL.createObjectURL(cachedBlob) };
          }
          try {
            const blob = await aptGenerationApi.getHolePng(hole.id);
            await setCachedBlob(cacheKey, blob);
            return { holeId: hole.id, imageUrl: URL.createObjectURL(blob) };
          } catch {
            return null;
          }
        });

        const procedureIdsWithPng = new Set<string>();
        for (const hole of holes) {
          for (const proc of hole.procedures ?? []) {
            if (proc.png_s3_url) procedureIdsWithPng.add(proc.id);
          }
        }
        const procedureThumbnailPromises = Array.from(procedureIdsWithPng).map(async (procedureId) => {
          const cacheKey = `procedure_png_${procedureId}`;
          const cachedBlob = await getCachedBlob(cacheKey);
          if (cachedBlob) {
            return { procedureId, imageUrl: URL.createObjectURL(cachedBlob) };
          }
          try {
            const blob = await aptGenerationApi.getProcedurePng(procedureId);
            await setCachedBlob(cacheKey, blob);
            return { procedureId, imageUrl: URL.createObjectURL(blob) };
          } catch {
            return null;
          }
        });

        Promise.all([Promise.all(holeThumbnailPromises), Promise.all(procedureThumbnailPromises)]).then(
          ([holeResults, procedureResults]) => {
            const newThumbnails: Record<string, string> = {};
            for (const result of holeResults) {
              if (result) newThumbnails[result.holeId] = result.imageUrl;
            }
            setPreviewThumbnails((prev) => ({ ...prev, ...newThumbnails }));
            const newProcedureThumbnails: Record<string, string> = {};
            for (const result of procedureResults) {
              if (result) newProcedureThumbnails[result.procedureId] = result.imageUrl;
            }
            setPreviewProcedureThumbnails((prev) => ({ ...prev, ...newProcedureThumbnails }));
          }
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentExtractionId, fetchHoles]);

  // Tab badge counts: use len(holesData[id]) when full list is loaded; else holes-count API (cached).
  useEffect(() => {
    let cancelled = false;

    const fetchHolesCounts = async () => {
      const cacheKey = `holes_counts_${projectId}`;
      const cacheExpiry = 5 * 60 * 1000; // 5 minutes

      const detectionIdList = aptPanelDetections.map((d) => d.id);
      const currentDetectionIds = new Set(detectionIdList);

      const countsFromLoadedHoles: Record<string, number> = {};
      for (const id of detectionIdList) {
        if (hasLoadedHolesList(holesData, id)) {
          countsFromLoadedHoles[id] = holesData[id].length;
        }
      }

      const idsNeedingApiCount = detectionIdList.filter((id) => !hasLoadedHolesList(holesData, id));

      const mergeAndSet = (apiOrCachedCounts: Record<string, number>, persistToCache: boolean) => {
        const merged: Record<string, number> = { ...countsFromLoadedHoles };
        for (const id of detectionIdList) {
          if (merged[id] !== undefined) {
            continue;
          }
          if (apiOrCachedCounts[id] !== undefined) {
            merged[id] = apiOrCachedCounts[id];
          }
        }
        if (cancelled) {
          return;
        }
        setHolesCounts(merged);
        if (persistToCache && detectionIdList.length > 0) {
          try {
            localStorage.setItem(
              cacheKey,
              JSON.stringify({
                counts: merged,
                timestamp: Date.now(),
                detectionIds: detectionIdList,
              })
            );
          } catch (_error) {
            // localStorage might be full or disabled, continue anyway
          }
        }
      };

      if (detectionIdList.length === 0) {
        return;
      }

      if (idsNeedingApiCount.length === 0) {
        mergeAndSet({}, true);
        return;
      }

      try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const { counts, timestamp, detectionIds } = JSON.parse(cached) as {
            counts: Record<string, number>;
            timestamp: number;
            detectionIds: string[];
          };
          const now = Date.now();
          const cachedIds = new Set(detectionIds || []);
          const idsMatch =
            currentDetectionIds.size === cachedIds.size &&
            Array.from(currentDetectionIds).every((id) => cachedIds.has(id));

          if (now - timestamp < cacheExpiry && idsMatch) {
            const validCounts: Record<string, number> = {};
            for (const id of idsNeedingApiCount) {
              if (counts[id] !== undefined) {
                validCounts[id] = counts[id];
              }
            }
            const stillMissing = idsNeedingApiCount.filter((id) => validCounts[id] === undefined);
            if (stillMissing.length === 0) {
              mergeAndSet(validCounts, false);
              return;
            }
            const missingPromises = stillMissing.map(async (id) => {
              try {
                const holesCount = await detectionsApi.getHolesCount(id);
                return { id, count: holesCount };
              } catch (_error) {
                return { id, count: 0 };
              }
            });
            const missingResults = await Promise.all(missingPromises);
            for (const result of missingResults) {
              validCounts[result.id] = result.count;
            }
            mergeAndSet(validCounts, true);
            return;
          }
        }
      } catch (_error) {
        // Cache invalid or doesn't exist, continue to fetch
      }

      const countPromises = idsNeedingApiCount.map(async (id) => {
        try {
          const holesCount = await detectionsApi.getHolesCount(id);
          return { id, count: holesCount };
        } catch (_error) {
          return { id, count: 0 };
        }
      });
      const results = await Promise.all(countPromises);
      const fetched: Record<string, number> = {};
      for (const result of results) {
        fetched[result.id] = result.count;
      }
      mergeAndSet(fetched, true);
    };

    fetchHolesCounts().catch(() => {
      /* best-effort; errors already handled per request inside fetchHolesCounts */
    });
    return () => {
      cancelled = true;
    };
  }, [aptPanelDetections, projectId, holesData]);

  const handleViewProcedureAPTSource = async (e: React.MouseEvent, procedureId: string) => {
    e.stopPropagation();
    setAptSourceHoleId(procedureId); // reuse for dialog title; could be procedure id
    setAptSourceFilename(null);
    try {
      const blob = await aptGenerationApi.getProcedureHProgram(procedureId);
      const text = await blob.text();
      setAptSourceContent(text);
      setShowAPTSourceDialog(true);
    } catch (_error) {
      toast.error(t('projectDetail.previewError') || 'Failed to load the Heidenhain program');
    }
  };

  const handleViewProcedureHFile = async (e: React.MouseEvent, procedureId: string, filename: string) => {
    e.stopPropagation();
    setAptSourceHoleId(procedureId);
    setAptSourceFilename(filename);
    try {
      const blob = await aptGenerationApi.getProcedureHFile(procedureId, filename);
      const text = await blob.text();
      setAptSourceContent(text);
      setShowAPTSourceDialog(true);
    } catch (_error) {
      toast.error(t('projectDetail.previewError') || 'Failed to load file');
    }
  };

  const handleDownloadProcedureHFilesZip = async (e: React.MouseEvent, procedureId: string) => {
    e.stopPropagation();
    try {
      const response = await aptGenerationApi.downloadProcedureHFilesZip(procedureId);
      let filename = `h-files_${procedureId}.zip`;
      const contentDisposition = response.headers?.['content-disposition'];
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (match?.[1]) {
          filename = match[1].trim();
        }
      }
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (_error) {
      toast.error(t('projectDetail.downloadError') || 'Failed to download H files zip');
    }
  };

  const handleViewPreview = async (e: React.MouseEvent, hole: HoleObjectModel) => {
    e.stopPropagation(); // Prevent row expansion when clicking preview button
    setPreviewHoleId(hole.id);

    // Use thumbnail if available, otherwise load full image (with cache)
    if (previewThumbnails[hole.id]) {
      setPreviewImageUrl(previewThumbnails[hole.id]);
    } else {
      try {
        const { getCachedBlob, setCachedBlob } = await import('@/utils/blobCache');
        const cacheKey = `png_${hole.id}`;

        // Try cache first
        let blob = await getCachedBlob(cacheKey);
        if (!blob) {
          // Not in cache, fetch from API
          blob = await aptGenerationApi.getHolePng(hole.id);
          // Cache the blob
          await setCachedBlob(cacheKey, blob);
        }

        const imageUrl = URL.createObjectURL(blob);
        setPreviewImageUrl(imageUrl);
        setPreviewThumbnails((prev) => ({ ...prev, [hole.id]: imageUrl }));
      } catch (_error) {
        setPreviewImageUrl(null);
        toast.error(t('projectDetail.previewError') || 'Failed to load preview');
      }
    }
  };

  const handleClosePreview = () => {
    setPreviewHoleId(null);
    setPreviewImageUrl(null);
    setPreviewProcedureId(null);
  };

  const handleViewProcedurePreview = (e: React.MouseEvent, procedureId: string) => {
    e.stopPropagation();
    setPreviewProcedureId(procedureId);
  };

  const handleSaveProcedureName = async (extractionId: string, procedureId: string, newName: string) => {
    const trimmed = newName.trim();
    try {
      await aptGenerationApi.updateProcedure(procedureId, {
        procedure_name: trimmed || null,
      });
      setHolesData((prev) => {
        const holes = prev[extractionId];
        if (!holes) return prev;
        return {
          ...prev,
          [extractionId]: holes.map((hole) => ({
            ...hole,
            procedures: (hole.procedures ?? []).map((p) =>
              p.id === procedureId ? { ...p, procedure_name: trimmed || null } : p
            ),
          })),
        };
      });
      setEditingProcedureId(null);
      setEditingProcedureName('');
      toast.success(t('common.success') || 'Saved');
    } catch {
      toast.error(t('common.error') || 'Failed to update procedure name');
    }
  };

  const handleSwapProcedures = async (extractionId: string, procedureIdA: string, procedureIdB: string) => {
    if (procedureIdA === procedureIdB) return;
    setSwappingExtractionId(extractionId);
    try {
      await aptGenerationApi.swapProcedureOrder(procedureIdA, procedureIdB);
      const allHoles = await fetchHoles(extractionId);
      const holes = allHoles.filter((h) => h.object_type !== 'Chamfer');
      setHolesData((prev) => ({ ...prev, [extractionId]: holes }));
      toast.success(t('projectDetail.procedureOrderSwapped') ?? 'Pořadí prohozeno');
    } catch {
      toast.error(t('projectDetail.procedureOrderSwapError') ?? 'Nepodařilo prohodit pořadí');
    } finally {
      setSwappingExtractionId(null);
    }
  };

  const handleDownloadAptSourcesZip = async () => {
    setIsDownloadingZip(true);
    try {
      // The project's Heidenhain programs — there is no APT to download any more.
      const response = await aptGenerationApi.downloadProjectHFilesZip(projectId);

      // Extract filename from Content-Disposition header or use fallback
      let filename = `h_files_project_${projectId}.zip`;
      const contentDisposition = response.headers?.['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch?.[1]) {
          filename = filenameMatch[1];
        }
      }

      const blob = response.data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (_error) {
      toast.error(t('projectDetail.downloadError') || 'Failed to download APT source archive');
    } finally {
      setIsDownloadingZip(false);
    }
  };

  // Cleanup preview thumbnails on unmount
  useEffect(() => {
    return () => {
      setPreviewThumbnails((current) => {
        Object.values(current).forEach((url) => URL.revokeObjectURL(url));
        return {};
      });
      setPreviewProcedureThumbnails((current) => {
        Object.values(current).forEach((url) => URL.revokeObjectURL(url));
        return {};
      });
      if (previewImageUrl && previewHoleId && previewThumbnails[previewHoleId] !== previewImageUrl) {
        URL.revokeObjectURL(previewImageUrl);
      }
    };
  }, []);

  // View mode toggle (holes/procedures) – shown when panel is visible
  const isToggleAvailable = !isRegenerating;

  // Notify parent when APT Generation is ready: detections loaded, no regen, and active tab content (holes) loaded
  const activeTabContentLoaded = !currentExtractionId || holesData[currentExtractionId] !== undefined;
  const aptGenerationReady = !isLoadingDetections && !isRegenerating && activeTabContentLoaded;
  useEffect(() => {
    onAptGenerationReady?.(aptGenerationReady);
    return () => {
      onAptGenerationReady?.(false);
    };
  }, [aptGenerationReady, onAptGenerationReady]);

  /** The panel heading, identical in every state the panel can be in. */
  const panelHeading = (
    <div className="flex min-w-0 items-center gap-3">
      <span className={ICON_TILE}>
        <Target size={18} aria-hidden="true" />
      </span>
      <h2 className={`m-0 ${PANEL_TITLE}`}>{t('projectDetail.aptGeneration')}</h2>
    </div>
  );

  // Show loading when loading detections or regenerating
  if (isLoadingDetections || isRegenerating) {
    return (
      <div className={`${CARD_PADDED} flex flex-col gap-4`}>
        {panelHeading}
        {isRegenerating && <p className="m-0 text-sm text-gray-500">{t('projectDetail.regeneratingAffectsAPT')}</p>}
        <TableSkeleton rows={HOLES_PER_PAGE} columns={6} />
      </div>
    );
  }

  if (detectionsError) {
    return (
      <div className={`${CARD_PADDED} flex flex-col gap-4`}>
        {panelHeading}
        <ErrorMessage message={detectionsError} />
      </div>
    );
  }

  if (sortedDetections.length === 0) {
    return (
      <div className={CARD_PADDED}>
        {panelHeading}
        <EmptyState
          title={t('projectDetail.aptGeneration')}
          message={t('projectDetail.aptGenerationArea')}
          icon={<Target size={28} aria-hidden="true" />}
        />
      </div>
    );
  }

  const handleViewModeChange = (mode: 'holes' | 'procedures') => {
    setViewMode(mode);
    const extractionId = sortedDetections[activeTabIndex]?.id;
    if (extractionId) setCurrentPage((prev) => ({ ...prev, [extractionId]: 0 }));
  };

  return (
    <div className={`${CARD_PADDED} flex flex-col gap-4`}>
      {/* Header: what the panel is on the left, what you can do with it on the right */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        {panelHeading}
        {isToggleAvailable && (
          <div className="flex flex-wrap items-center gap-2">
            <div
              className="inline-flex gap-1 rounded-lg bg-gray-100 p-1"
              role="group"
              aria-label={t('projectDetail.viewBy')}
            >
              <button
                type="button"
                onClick={() => handleViewModeChange('holes')}
                className={`${SEGMENT_BASE} ${viewMode === 'holes' ? SEGMENT_ACTIVE : SEGMENT_INACTIVE}`}
                title={t('projectDetail.viewByHoles')}
                aria-pressed={viewMode === 'holes'}
              >
                <Circle size={14} aria-hidden="true" />
                <span>{t('projectDetail.holes')}</span>
              </button>
              <button
                type="button"
                onClick={() => handleViewModeChange('procedures')}
                className={`${SEGMENT_BASE} ${viewMode === 'procedures' ? SEGMENT_ACTIVE : SEGMENT_INACTIVE}`}
                title={t('projectDetail.viewByProcedures')}
                aria-pressed={viewMode === 'procedures'}
              >
                <ListBullets size={14} aria-hidden="true" />
                <span>{t('projectDetail.procedures')}</span>
              </button>
            </div>
            <ActionButton
              variant="secondary"
              icon={<DownloadSimple size={16} aria-hidden="true" />}
              onClick={handleDownloadAptSourcesZip}
              loading={isDownloadingZip}
              title={t('projectDetail.downloadProjectAptSources')}
              aria-label={t('projectDetail.downloadProjectAptSources')}
              className="shrink-0"
            >
              {t('projectDetail.downloadAptZip')}
            </ActionButton>
          </div>
        )}
      </div>

      {/* Extractions as tabs */}
      {sortedDetections.length > 0 && (
        <div className={TAB_STRIP_WRAPPER}>
          <TabView
            activeIndex={activeTabIndex}
            onTabChange={(e) => {
              setActiveTabIndex(e.index);
              const extractionId = sortedDetections[e.index]?.id;
              if (extractionId) {
                setCurrentPage((prev) => ({ ...prev, [extractionId]: 0 }));
              }
            }}
          >
            {sortedDetections.map((extraction, tabIndex) => {
              const isActiveTab = tabIndex === activeTabIndex;
              const holes = holesData[extraction.id] || [];
              const sortedHoles = [...holes].sort((a, b) => a.order_number - b.order_number);
              const isLoadingHolesForExtraction = !holesData[extraction.id] && isLoadingHoles;
              const tabHolesCount = hasLoadedHolesList(holesData, extraction.id)
                ? holesData[extraction.id].length
                : holesCounts[extraction.id] !== undefined
                  ? holesCounts[extraction.id]
                  : '...';
              const procedureGroups = [...buildProcedureGroups(holes)].sort(
                (a, b) => a.procedure.order_number - b.procedure.order_number
              );

              // Pagination: holes view vs procedures view
              const page = currentPage[extraction.id] || 0;
              const totalPagesHoles = Math.ceil(sortedHoles.length / HOLES_PER_PAGE);
              const totalPagesProcedures = Math.ceil(procedureGroups.length / PROCEDURES_PER_PAGE);
              const totalPages = viewMode === 'procedures' ? totalPagesProcedures : totalPagesHoles;
              const startIndex = page * (viewMode === 'procedures' ? PROCEDURES_PER_PAGE : HOLES_PER_PAGE);
              const endIndex = startIndex + (viewMode === 'procedures' ? PROCEDURES_PER_PAGE : HOLES_PER_PAGE);
              const paginatedHoles = sortedHoles.slice(
                viewMode === 'holes' ? startIndex : 0,
                viewMode === 'holes' ? endIndex : sortedHoles.length
              );
              const paginatedProcedureGroups = procedureGroups.slice(
                viewMode === 'procedures' ? startIndex : 0,
                viewMode === 'procedures' ? endIndex : procedureGroups.length
              );

              // Pad with empty rows for consistent table height
              const rowsPerPage = viewMode === 'procedures' ? PROCEDURES_PER_PAGE : HOLES_PER_PAGE;
              const emptyRowsCount =
                rowsPerPage - (viewMode === 'procedures' ? paginatedProcedureGroups.length : paginatedHoles.length);
              const emptyRows = Array.from({ length: Math.max(0, emptyRowsCount) }, (_, i) => ({
                id: `empty-${i}`,
                isEmpty: true,
              }));

              const handlePageChange = (newPage: number) => {
                if (newPage >= 0 && newPage < totalPages) {
                  setCurrentPage((prev) => ({ ...prev, [extraction.id]: newPage }));
                }
              };

              return (
                <TabPanel
                  key={extraction.id}
                  pt={{ header: { className: isActiveTab ? TAB_HEADER_ACTIVE : undefined } }}
                  header={
                    <span className="flex items-center gap-2">
                      <span>{extraction.orientation}</span>
                      <span className="text-xs tabular-nums text-gray-400">({tabHolesCount})</span>
                      {isActiveTab && (
                        <span className="absolute inset-x-0 bottom-0 h-0.5 bg-primary" aria-hidden="true" />
                      )}
                    </span>
                  }
                >
                  {isLoadingHolesForExtraction ? (
                    <TableSkeleton rows={HOLES_PER_PAGE} columns={6} />
                  ) : sortedHoles.length === 0 ? (
                    <EmptyState
                      title={t('projectDetail.holes')}
                      message={t('projectDetail.noHolesFound')}
                      icon={<Target size={28} aria-hidden="true" />}
                    />
                  ) : viewMode === 'procedures' ? (
                    /* Procedure perspective: # leftmost, then Procedure specs (9 cols, last is placeholder), then Hole specs (3 cols) right; vertical line left of Hole specs */
                    <div className={`${TABLE_CARD} [contain:paint]`}>
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[64rem] table-fixed border-collapse text-sm">
                          <colgroup>
                            <col className="w-10" />
                            <col className="w-14" />
                            <col className="w-36" />
                            <col className="w-20" />
                            <col className="w-24" />
                            <col className="w-24" />
                            <col className="w-22" />
                            <col className="w-22" />
                            <col className="w-12" />
                            <col className="w-24" />
                            <col className="w-12" />
                            <col className="w-16" />
                            <col className="w-16" />
                            <col className="w-16" />
                          </colgroup>
                          <thead>
                            <tr className="border-b border-gray-200 bg-[#fafbfb]">
                              <th className={GROUP_LABEL_CELL} colSpan={11}>
                                {t('projectDetail.procedureSpecifications')}
                              </th>
                              <th className={`${GROUP_LABEL_CELL} border-l border-gray-200`} colSpan={3}>
                                {t('projectDetail.holeSpecifications')}
                              </th>
                            </tr>
                            <tr className={TABLE_HEADER_ROW}>
                              <th
                                className={HEADER_CELL}
                                aria-label={t('projectDetail.reorderProcedures') ?? 'Změna pořadí'}
                              />
                              <th className={`${HEADER_CELL} whitespace-nowrap`}>#</th>
                              <th className={HEADER_CELL}>
                                {t('projectDetail.procedureNameLine1')}
                                <br />
                                {t('projectDetail.procedureNameLine2')}
                              </th>
                              <th className={HEADER_CELL_NUM}>
                                {t('projectDetail.toolNumberLine1')}
                                <br />
                                {t('projectDetail.toolNumberLine2')}
                              </th>
                              <th className={HEADER_CELL_NUM}>
                                {t('projectDetail.toolDiameterLine1')}
                                <br />
                                {t('projectDetail.toolDiameterLine2')}
                              </th>
                              <th className={HEADER_CELL_NUM}>
                                {t('projectDetail.totalDepthLine1')}
                                <br />
                                {t('projectDetail.totalDepthLine2')}
                              </th>
                              <th className={HEADER_CELL}>
                                {t('projectDetail.moTypeLine1')}
                                <br />
                                {t('projectDetail.moTypeLine2')}
                              </th>
                              <th className={HEADER_CELL_NUM}>
                                {t('projectDetail.spindleSpeedLine1')}
                                <br />
                                {t('projectDetail.spindleSpeedLine2')}
                              </th>
                              <th className={HEADER_CELL_NUM}>
                                {t('projectDetail.holesCountLine1')}
                                <br />
                                {t('projectDetail.holesCountLine2')}
                              </th>
                              <th className={`${HEADER_CELL} whitespace-nowrap`}>{t('projectDetail.preview')}</th>
                              <th className={HEADER_CELL} aria-hidden="true" />
                              <th className={`${HEADER_CELL_NUM} whitespace-nowrap border-l border-gray-200`}>
                                {t('projectDetail.xMm')}
                              </th>
                              <th className={`${HEADER_CELL_NUM} whitespace-nowrap`}>{t('projectDetail.yMm')}</th>
                              <th className={HEADER_CELL_NUM}>
                                {t('projectDetail.diameterMmLine1')}
                                <br />
                                {t('projectDetail.diameterMmLine2')}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedProcedureGroups.map((group) => {
                              const isExpanded = expandedProcedures.has(group.key);
                              const hasHoles = group.holes.length > 0;
                              const isSwapping = swappingExtractionId === extraction.id;
                              return (
                                <>
                                  <tr
                                    key={group.key}
                                    className={`${TABLE_ROW} ${hasHoles ? 'cursor-pointer' : ''}`}
                                    title={hasHoles ? t('projectDetail.clickToExpandHoles') || undefined : undefined}
                                    onClick={() => {
                                      if (hasHoles) {
                                        setExpandedProcedures((prev) => {
                                          const next = new Set(prev);
                                          if (next.has(group.key)) next.delete(group.key);
                                          else next.add(group.key);
                                          return next;
                                        });
                                        setExpandedProcedureHolesPage((prev) => {
                                          const next = { ...prev };
                                          delete next[group.key];
                                          return next;
                                        });
                                      }
                                    }}
                                    onDragOver={(e) => {
                                      e.preventDefault();
                                      e.dataTransfer.dropEffect = 'move';
                                    }}
                                    onDrop={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const draggedId = e.dataTransfer.getData('text/plain');
                                      if (draggedId && draggedId !== group.procedure.id) {
                                        handleSwapProcedures(extraction.id, draggedId, group.procedure.id);
                                      }
                                    }}
                                  >
                                    <td className={`${TABLE_CELL} text-gray-400`} onClick={(e) => e.stopPropagation()}>
                                      <span
                                        draggable={!isSwapping}
                                        onDragStart={(e) => {
                                          e.stopPropagation();
                                          e.dataTransfer.setData('text/plain', group.procedure.id);
                                          e.dataTransfer.effectAllowed = 'move';
                                        }}
                                        className={`inline-flex cursor-grab touch-none active:cursor-grabbing ${MOTION} ${isSwapping ? 'pointer-events-none opacity-50' : 'hover:text-gray-600'}`}
                                        title={
                                          t('projectDetail.dragToReorderProcedures') ?? 'Přetáhni pro prohození pořadí'
                                        }
                                        aria-label={t('projectDetail.dragToReorderProcedures') ?? 'Změna pořadí řádku'}
                                      >
                                        <DotsSixVertical size={16} aria-hidden="true" />
                                      </span>
                                    </td>
                                    <td className={`${TABLE_CELL} text-gray-900`}>
                                      <div className="flex items-center gap-1">
                                        {hasHoles ? (
                                          isExpanded ? (
                                            <CaretDown
                                              size={14}
                                              className="shrink-0 text-gray-400"
                                              aria-hidden="true"
                                            />
                                          ) : (
                                            <CaretRight
                                              size={14}
                                              className="shrink-0 text-gray-400"
                                              aria-hidden="true"
                                            />
                                          )
                                        ) : (
                                          <span className="w-3.5 shrink-0" aria-hidden="true" />
                                        )}
                                        <span className="tabular-nums">{group.procedure.order_number}</span>
                                      </div>
                                    </td>
                                    <td
                                      className={`${TABLE_CELL} min-w-0 overflow-hidden break-words hyphens-auto`}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {editingProcedureId === group.procedure.id ? (
                                        <div className="flex items-center gap-1">
                                          <input
                                            type="text"
                                            value={editingProcedureName}
                                            onChange={(e) => setEditingProcedureName(e.target.value)}
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter') {
                                                handleSaveProcedureName(
                                                  extraction.id,
                                                  group.procedure.id,
                                                  editingProcedureName
                                                );
                                              }
                                              if (e.key === 'Escape') {
                                                setEditingProcedureId(null);
                                                setEditingProcedureName('');
                                              }
                                            }}
                                            className={`min-w-[8rem] rounded-lg border border-gray-200 px-2 py-1 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40 ${MOTION}`}
                                            autoFocus={true}
                                            aria-label={t('projectDetail.procedureName')}
                                          />
                                          <button
                                            type="button"
                                            onClick={() =>
                                              handleSaveProcedureName(
                                                extraction.id,
                                                group.procedure.id,
                                                editingProcedureName
                                              )
                                            }
                                            className={`rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 active:scale-[0.98] ${MOTION}`}
                                            title={t('common.save')}
                                            aria-label={t('common.save')}
                                          >
                                            <Check size={14} aria-hidden="true" />
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setEditingProcedureId(null);
                                              setEditingProcedureName('');
                                            }}
                                            className={`rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 active:scale-[0.98] ${MOTION}`}
                                            title={t('common.cancel')}
                                            aria-label={t('common.cancel')}
                                          >
                                            <X size={14} aria-hidden="true" />
                                          </button>
                                        </div>
                                      ) : (
                                        <div className="flex min-w-0 items-center gap-2">
                                          <span className="min-w-0 break-words hyphens-auto">
                                            {group.procedure.procedure_name ?? group.procedure.assembly.assembly_id}
                                          </span>
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setEditingProcedureId(group.procedure.id);
                                              setEditingProcedureName(
                                                group.procedure.procedure_name ??
                                                  group.procedure.assembly.assembly_id ??
                                                  ''
                                              );
                                            }}
                                            className={`rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 active:scale-[0.98] ${MOTION}`}
                                            title={t('projectDetail.editProcedureName')}
                                            aria-label={t('projectDetail.editProcedureName')}
                                          >
                                            <PencilSimple size={14} aria-hidden="true" />
                                          </button>
                                        </div>
                                      )}
                                    </td>
                                    <td className={TABLE_CELL_NUM}>
                                      {group.procedure.assembly.tool?.tool_number_unique}
                                    </td>
                                    <td className={TABLE_CELL_NUM}>
                                      {group.procedure.assembly.tool?.tool_diameter != null
                                        ? group.procedure.assembly.tool.tool_diameter.toFixed(2)
                                        : ''}
                                    </td>
                                    <td className={TABLE_CELL_NUM}>{group.procedure.total_depth.toFixed(2)}</td>
                                    <td className={`${TABLE_CELL} break-words hyphens-auto`}>
                                      {group.procedure.mo_type}
                                    </td>
                                    <td className={TABLE_CELL_NUM}>{group.procedure.spindle_speed_rpm.toFixed(0)}</td>
                                    <td className={TABLE_CELL_NUM}>{group.holes.length}</td>
                                    <td className={TABLE_CELL}>
                                      <div className="flex items-center gap-2">
                                        <span
                                          className={`inline-flex shrink-0 cursor-pointer rounded hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 active:scale-[0.98] ${MOTION}`}
                                          role="button"
                                          tabIndex={0}
                                          title={t('projectDetail.view3DPreview') || 'Zobrazit 3D náhled'}
                                          aria-label={t('projectDetail.view3DPreview') || 'Zobrazit 3D náhled'}
                                          onClick={(e) => handleViewProcedurePreview(e, group.procedure.id)}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              handleViewProcedurePreview(
                                                e as unknown as React.MouseEvent,
                                                group.procedure.id
                                              );
                                            }
                                          }}
                                        >
                                          {previewProcedureThumbnails[group.procedure.id] ? (
                                            <img
                                              src={previewProcedureThumbnails[group.procedure.id]}
                                              alt=""
                                              className={`${PREVIEW_BOX} pointer-events-none object-contain`}
                                              onError={(e) => {
                                                e.currentTarget.style.display = 'none';
                                              }}
                                            />
                                          ) : (
                                            <span
                                              className={PREVIEW_BOX}
                                              title={
                                                group.procedure.apt_s3_url
                                                  ? t('projectDetail.viewAPTSource') || 'View APT source'
                                                  : t('projectDetail.view3DPreview') || 'Zobrazit 3D náhled'
                                              }
                                              aria-hidden="true"
                                            />
                                          )}
                                        </span>
                                        {group.procedure.apt_s3_url && (
                                          <ActionButton
                                            variant="ghost"
                                            iconOnly={true}
                                            icon={<FileText size={14} aria-hidden="true" />}
                                            onClick={(e) => handleViewProcedureAPTSource(e, group.procedure.id)}
                                            title={t('projectDetail.viewAPTSource') || 'View APT source'}
                                            aria-label={t('projectDetail.viewAPTSource') || 'View APT source'}
                                            className="shrink-0"
                                          />
                                        )}
                                      </div>
                                    </td>
                                    <td className={TABLE_CELL} />
                                    <td className={`${TABLE_CELL} border-l border-gray-200`} />
                                    <td className={TABLE_CELL} />
                                    <td className={TABLE_CELL} />
                                  </tr>
                                  {isExpanded &&
                                    (() => {
                                      const holesPage = expandedProcedureHolesPage[group.key] ?? 0;
                                      const paginatedExpandedHoles = group.holes.slice(
                                        holesPage * EXPANDED_HOLES_PER_PAGE,
                                        (holesPage + 1) * EXPANDED_HOLES_PER_PAGE
                                      );
                                      const totalExpandedPages = Math.ceil(
                                        group.holes.length / EXPANDED_HOLES_PER_PAGE
                                      );
                                      const hasExpandedPagination = group.holes.length > EXPANDED_HOLES_PER_PAGE;
                                      const hFilesList = group.procedure.h_files ?? [];
                                      const hasHFilesContent = hFilesList.length > 0 || !!group.procedure.h_files_pplog;
                                      const lastHolesPage = totalExpandedPages - 1;
                                      const showHFilesHeaderRow = hasHFilesContent && holesPage === 0;
                                      const pairHFileWithHoleColumn = hasHFilesContent && holesPage === 0;
                                      return (
                                        <>
                                          {showHFilesHeaderRow && (
                                            <tr key={`${group.key}-h-files-header`} className={SUB_ROW}>
                                              <td
                                                className={`${SUB_CELL} border-r border-gray-200`}
                                                colSpan={11}
                                                onClick={(ev) => ev.stopPropagation()}
                                              >
                                                <div className="flex w-full min-w-0 items-center gap-2 px-6">
                                                  <span className={`shrink-0 ${SECTION_LABEL}`}>
                                                    {t('projectDetail.hFilesSectionLabel')}
                                                  </span>
                                                  <div className="ml-auto flex flex-wrap items-center justify-end gap-1">
                                                    {group.procedure.h_files_pplog && (
                                                      <ActionButton
                                                        variant="ghost"
                                                        icon={<FileText size={14} aria-hidden="true" />}
                                                        onClick={(e) =>
                                                          handleViewProcedureHFile(
                                                            e,
                                                            group.procedure.id,
                                                            group.procedure.h_files_pplog!
                                                          )
                                                        }
                                                        title={t('projectDetail.viewPplogFile')}
                                                        aria-label={t('projectDetail.viewPplogFile')}
                                                        className="min-w-0 max-w-full px-2 py-1 text-xs"
                                                      >
                                                        <span className="min-w-0 truncate">
                                                          {group.procedure.h_files_pplog}
                                                        </span>
                                                      </ActionButton>
                                                    )}
                                                    <ActionButton
                                                      variant="ghost"
                                                      icon={<DownloadSimple size={14} aria-hidden="true" />}
                                                      onClick={(e) =>
                                                        handleDownloadProcedureHFilesZip(e, group.procedure.id)
                                                      }
                                                      title={t('projectDetail.downloadHFilesZipTooltip')}
                                                      aria-label={t('projectDetail.downloadHFilesZipTooltip')}
                                                      className="px-2 py-1 text-xs"
                                                    >
                                                      {t('projectDetail.downloadHFilesZipButton')}
                                                    </ActionButton>
                                                  </div>
                                                </div>
                                              </td>
                                              <td
                                                className={`${SUB_CELL} border-l border-gray-200`}
                                                colSpan={3}
                                                aria-hidden="true"
                                              />
                                            </tr>
                                          )}
                                          {paginatedExpandedHoles.map((hole, rowIndex) => {
                                            const globalHoleIndex = holesPage * EXPANDED_HOLES_PER_PAGE + rowIndex;
                                            const hFileForRow = pairHFileWithHoleColumn
                                              ? hFilesList[globalHoleIndex]
                                              : undefined;
                                            return (
                                              <tr key={hole.id} className={SUB_ROW}>
                                                <td
                                                  className={`${SUB_CELL} border-r border-gray-200`}
                                                  colSpan={11}
                                                  onClick={(ev) => ev.stopPropagation()}
                                                >
                                                  {hFileForRow ? (
                                                    <div className="flex min-w-0 items-center gap-2 px-6">
                                                      <span className="min-w-0 break-all hyphens-auto text-xs">
                                                        {hFileForRow}
                                                      </span>
                                                      <ActionButton
                                                        variant="ghost"
                                                        iconOnly={true}
                                                        icon={<FileText size={14} aria-hidden="true" />}
                                                        onClick={(e) =>
                                                          handleViewProcedureHFile(e, group.procedure.id, hFileForRow)
                                                        }
                                                        title={t('projectDetail.viewHFile')}
                                                        aria-label={t('projectDetail.viewHFile')}
                                                        className="shrink-0"
                                                      />
                                                    </div>
                                                  ) : null}
                                                </td>
                                                <td className={`${SUB_CELL_NUM} border-l border-gray-200`}>
                                                  {hole.x_mm.toFixed(2)}
                                                </td>
                                                <td className={SUB_CELL_NUM}>{hole.y_mm.toFixed(2)}</td>
                                                <td className={SUB_CELL_NUM}>{hole.diameter_mm.toFixed(2)}</td>
                                              </tr>
                                            );
                                          })}
                                          {holesPage === lastHolesPage &&
                                            pairHFileWithHoleColumn &&
                                            hFilesList.length > group.holes.length &&
                                            hFilesList.slice(group.holes.length).map((hFileName, idx) => (
                                              <tr key={`${group.key}-h-file-only-${idx}`} className={SUB_ROW}>
                                                <td
                                                  className={`${SUB_CELL} border-r border-gray-200`}
                                                  colSpan={11}
                                                  onClick={(ev) => ev.stopPropagation()}
                                                >
                                                  <div className="flex min-w-0 items-center gap-2 px-6">
                                                    <span className="min-w-0 break-all hyphens-auto text-xs">
                                                      {hFileName}
                                                    </span>
                                                    <ActionButton
                                                      variant="ghost"
                                                      iconOnly={true}
                                                      icon={<FileText size={14} aria-hidden="true" />}
                                                      onClick={(e) =>
                                                        handleViewProcedureHFile(e, group.procedure.id, hFileName)
                                                      }
                                                      title={t('projectDetail.viewHFile')}
                                                      aria-label={t('projectDetail.viewHFile')}
                                                      className="shrink-0"
                                                    />
                                                  </div>
                                                </td>
                                                <td
                                                  className={`${SUB_CELL} border-l border-gray-200`}
                                                  colSpan={3}
                                                  aria-hidden="true"
                                                />
                                              </tr>
                                            ))}
                                          {hasExpandedPagination && (
                                            <tr className={SUB_ROW}>
                                              <td className={SUB_CELL} colSpan={11} />
                                              <td className={`${SUB_CELL} border-l border-gray-200`} colSpan={3}>
                                                <div className="flex items-center justify-between gap-2 text-xs text-gray-600">
                                                  <span className="tabular-nums">
                                                    {t('common.showing')} {holesPage * EXPANDED_HOLES_PER_PAGE + 1}–
                                                    {Math.min(
                                                      (holesPage + 1) * EXPANDED_HOLES_PER_PAGE,
                                                      group.holes.length
                                                    )}{' '}
                                                    {t('common.of')} {group.holes.length}
                                                  </span>
                                                  <div className="flex items-center gap-1">
                                                    <ActionButton
                                                      variant="ghost"
                                                      iconOnly={true}
                                                      icon={<CaretLeft size={14} aria-hidden="true" />}
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setExpandedProcedureHolesPage((prev) => ({
                                                          ...prev,
                                                          [group.key]: Math.max(0, (prev[group.key] ?? 0) - 1),
                                                        }));
                                                      }}
                                                      disabled={holesPage === 0}
                                                      aria-label={t('common.previousPage')}
                                                    />
                                                    <ActionButton
                                                      variant="ghost"
                                                      iconOnly={true}
                                                      icon={<CaretRight size={14} aria-hidden="true" />}
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setExpandedProcedureHolesPage((prev) => ({
                                                          ...prev,
                                                          [group.key]: Math.min(
                                                            totalExpandedPages - 1,
                                                            (prev[group.key] ?? 0) + 1
                                                          ),
                                                        }));
                                                      }}
                                                      disabled={holesPage >= totalExpandedPages - 1}
                                                      aria-label={t('common.nextPage')}
                                                    />
                                                  </div>
                                                </div>
                                              </td>
                                            </tr>
                                          )}
                                        </>
                                      );
                                    })()}
                                </>
                              );
                            })}
                            {emptyRows.map((emptyRow) => (
                              <tr key={emptyRow.id} className={EMPTY_ROW}>
                                <td className={TABLE_CELL} />
                                <td className={TABLE_CELL} />
                                <td className={TABLE_CELL} />
                                <td className={TABLE_CELL} />
                                <td className={TABLE_CELL} />
                                <td className={TABLE_CELL} />
                                <td className={TABLE_CELL} />
                                <td className={TABLE_CELL} />
                                <td className={TABLE_CELL} />
                                <td className={TABLE_CELL}>
                                  <span className="inline-block h-8 w-12" />
                                </td>
                                <td className={TABLE_CELL} />
                                <td className={`${TABLE_CELL} border-l border-gray-200`} />
                                <td className={TABLE_CELL} />
                                <td className={TABLE_CELL} />
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {totalPages > 1 && (
                        <div className={`${TABLE_FOOTER} flex-wrap gap-2`}>
                          <span className="text-sm tabular-nums text-gray-600">
                            {t('common.showing')} {startIndex + 1}–{Math.min(endIndex, procedureGroups.length)}{' '}
                            {t('common.of')} {procedureGroups.length}
                          </span>
                          <div className="flex items-center gap-1">
                            <ActionButton
                              variant="ghost"
                              iconOnly={true}
                              icon={<CaretLeft size={16} aria-hidden="true" />}
                              onClick={() => handlePageChange(page - 1)}
                              disabled={page === 0}
                              aria-label={t('common.previousPage')}
                            />
                            {Array.from({ length: totalPages }, (_, i) => i).map((pageNum) => {
                              const showPage =
                                pageNum === 0 ||
                                pageNum === totalPages - 1 ||
                                (pageNum >= page - 1 && pageNum <= page + 1);
                              if (!showPage && pageNum === 1 && page > 2)
                                return (
                                  <span key={pageNum} className="px-2 text-sm text-gray-400">
                                    …
                                  </span>
                                );
                              if (!showPage && pageNum === totalPages - 2 && page < totalPages - 3)
                                return (
                                  <span key={pageNum} className="px-2 text-sm text-gray-400">
                                    …
                                  </span>
                                );
                              if (!showPage) return null;
                              return (
                                <button
                                  key={pageNum}
                                  type="button"
                                  onClick={() => handlePageChange(pageNum)}
                                  className={`${PAGE_BUTTON_BASE} ${pageNum === page ? PAGE_BUTTON_CURRENT : PAGE_BUTTON_IDLE}`}
                                  aria-label={`${t('common.page')} ${pageNum + 1}`}
                                  aria-current={pageNum === page ? 'page' : undefined}
                                >
                                  {pageNum + 1}
                                </button>
                              );
                            })}
                            <ActionButton
                              variant="ghost"
                              iconOnly={true}
                              icon={<CaretRight size={16} aria-hidden="true" />}
                              onClick={() => handlePageChange(page + 1)}
                              disabled={page >= totalPages - 1}
                              aria-label={t('common.nextPage')}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className={`${TABLE_CARD} [contain:paint]`}>
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[64rem] table-fixed border-collapse text-sm">
                          <colgroup>
                            <col className="w-14" />
                            <col className="w-20" />
                            <col className="w-20" />
                            <col className="w-20" />
                            <col className="w-24" />
                            <col className="w-28" />
                            <col className="w-36" />
                            <col className="w-20" />
                            <col className="w-24" />
                            <col className="w-24" />
                            <col className="w-22" />
                            <col className="w-22" />
                          </colgroup>
                          <thead>
                            <tr className="border-b border-gray-200 bg-[#fafbfb]">
                              <th className={GROUP_LABEL_CELL} colSpan={6}>
                                {t('projectDetail.holeSpecifications')}
                              </th>
                              <th className={`${GROUP_LABEL_CELL} border-l border-gray-200`} colSpan={6}>
                                {t('projectDetail.procedureSpecifications')}
                              </th>
                            </tr>
                            <tr className={TABLE_HEADER_ROW}>
                              <th className={`${HEADER_CELL} whitespace-nowrap`}>#</th>
                              <th className={`${HEADER_CELL_NUM} whitespace-nowrap`}>{t('projectDetail.xMm')}</th>
                              <th className={`${HEADER_CELL_NUM} whitespace-nowrap`}>{t('projectDetail.yMm')}</th>
                              <th className={HEADER_CELL_NUM}>
                                {t('projectDetail.diameterMmLine1')}
                                <br />
                                {t('projectDetail.diameterMmLine2')}
                              </th>
                              <th className={`${HEADER_CELL} whitespace-nowrap`}>{t('projectDetail.preview')}</th>
                              <th className={`${HEADER_CELL_NUM} min-w-0 overflow-hidden break-words hyphens-auto`}>
                                {t('aptSources.procedureCountLine1')}
                                <br />
                                {t('aptSources.procedureCountLine2')}
                              </th>
                              <th className={`${HEADER_CELL} border-l border-gray-200`}>
                                {t('projectDetail.procedureNameLine1')}
                                <br />
                                {t('projectDetail.procedureNameLine2')}
                              </th>
                              <th className={HEADER_CELL_NUM}>
                                {t('projectDetail.toolNumberLine1')}
                                <br />
                                {t('projectDetail.toolNumberLine2')}
                              </th>
                              <th className={HEADER_CELL_NUM}>
                                {t('projectDetail.toolDiameterLine1')}
                                <br />
                                {t('projectDetail.toolDiameterLine2')}
                              </th>
                              <th className={HEADER_CELL_NUM}>
                                {t('projectDetail.totalDepthLine1')}
                                <br />
                                {t('projectDetail.totalDepthLine2')}
                              </th>
                              <th className={HEADER_CELL}>
                                {t('projectDetail.moTypeLine1')}
                                <br />
                                {t('projectDetail.moTypeLine2')}
                              </th>
                              <th className={HEADER_CELL_NUM}>
                                {t('projectDetail.spindleSpeedLine1')}
                                <br />
                                {t('projectDetail.spindleSpeedLine2')}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedHoles.map((hole) => {
                              const isHoleExpanded = expandedHoles.has(hole.id);
                              const hasProcedures = hole.procedures && hole.procedures.length > 0;
                              const hasAptError = !hasProcedures && Boolean(hole.apt_error);

                              return (
                                <Fragment key={hole.id}>
                                  <tr
                                    className={`${hasAptError ? `${ROW_BASE} bg-red-50` : TABLE_ROW} ${
                                      hasProcedures ? 'cursor-pointer' : ''
                                    }`}
                                    title={
                                      hasProcedures
                                        ? t('projectDetail.clickToExpandProcedures') || undefined
                                        : undefined
                                    }
                                    onClick={() => {
                                      if (hasProcedures) {
                                        setExpandedHoles((prev) => {
                                          const next = new Set(prev);
                                          if (next.has(hole.id)) {
                                            next.delete(hole.id);
                                          } else {
                                            next.add(hole.id);
                                          }
                                          return next;
                                        });
                                      }
                                    }}
                                  >
                                    <td className={`${TABLE_CELL} text-gray-900`}>
                                      <div className="flex items-center gap-1">
                                        {hasProcedures ? (
                                          isHoleExpanded ? (
                                            <CaretDown
                                              size={14}
                                              className="shrink-0 text-gray-400"
                                              aria-hidden="true"
                                            />
                                          ) : (
                                            <CaretRight
                                              size={14}
                                              className="shrink-0 text-gray-400"
                                              aria-hidden="true"
                                            />
                                          )
                                        ) : hasAptError ? (
                                          <WarningCircle
                                            size={14}
                                            className="shrink-0 text-red-600"
                                            aria-hidden="true"
                                          />
                                        ) : (
                                          <span className="w-3.5 shrink-0" aria-hidden="true" />
                                        )}
                                        <span className="tabular-nums">{hole.order_number}</span>
                                      </div>
                                    </td>
                                    <td className={TABLE_CELL_NUM}>{hole.x_mm.toFixed(2)}</td>
                                    <td className={TABLE_CELL_NUM}>{hole.y_mm.toFixed(2)}</td>
                                    <td className={TABLE_CELL_NUM}>{hole.diameter_mm.toFixed(2)}</td>
                                    <td
                                      className={`${TABLE_CELL} cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary`}
                                      role="button"
                                      tabIndex={0}
                                      title={t('projectDetail.viewPreview') || 'View preview'}
                                      aria-label={t('projectDetail.viewPreview') || 'View preview'}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewPreview(e, hole);
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          handleViewPreview(
                                            e as unknown as React.MouseEvent<HTMLTableCellElement>,
                                            hole
                                          );
                                        }
                                      }}
                                    >
                                      {previewThumbnails[hole.id] ? (
                                        <img
                                          src={previewThumbnails[hole.id]}
                                          alt=""
                                          className={`${PREVIEW_BOX} pointer-events-none object-contain`}
                                          onError={(e) => {
                                            e.currentTarget.style.display = 'none';
                                          }}
                                        />
                                      ) : (
                                        <span className={PREVIEW_BOX} aria-hidden="true" />
                                      )}
                                    </td>
                                    <td className={TABLE_CELL_NUM}>{hole.procedures?.length ?? 0}</td>
                                    <td
                                      className={`${TABLE_CELL} border-l border-gray-200 align-top`}
                                      colSpan={6}
                                      aria-hidden="true"
                                    />
                                  </tr>
                                  {hasAptError && (
                                    <tr className="border-b border-gray-100 bg-red-50">
                                      <td colSpan={12} className="py-2 pl-9 pr-3">
                                        <div className="flex items-start gap-2 text-xs text-red-700">
                                          <WarningCircle
                                            size={14}
                                            className="mt-0.5 shrink-0 text-red-600"
                                            aria-hidden="true"
                                          />
                                          <span className="break-all">{hole.apt_error}</span>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                  {/* Procedures rows – no preview in Holes mode (preview only for holes); first row tighter to parent; sorted by order_number */}
                                  {isHoleExpanded &&
                                    hasProcedures &&
                                    [...(hole.procedures ?? [])]
                                      .sort((a, b) => a.order_number - b.order_number)
                                      .map((procedure) => (
                                        <tr key={procedure.id} className={SUB_ROW}>
                                          <td className={SUB_CELL} />
                                          <td className={SUB_CELL} />
                                          <td className={SUB_CELL} />
                                          <td className={SUB_CELL} />
                                          <td className={SUB_CELL} />
                                          <td className={SUB_CELL} />
                                          <td
                                            className={`${SUB_CELL} min-w-0 overflow-hidden break-words hyphens-auto border-l border-gray-200`}
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            {editingProcedureId === procedure.id ? (
                                              <div className="flex items-center gap-1">
                                                <input
                                                  type="text"
                                                  value={editingProcedureName}
                                                  onChange={(e) => setEditingProcedureName(e.target.value)}
                                                  onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                      handleSaveProcedureName(
                                                        extraction.id,
                                                        procedure.id,
                                                        editingProcedureName
                                                      );
                                                    }
                                                    if (e.key === 'Escape') {
                                                      setEditingProcedureId(null);
                                                      setEditingProcedureName('');
                                                    }
                                                  }}
                                                  className={`min-w-[8rem] rounded-lg border border-gray-200 px-2 py-1 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40 ${MOTION}`}
                                                  autoFocus={true}
                                                  aria-label={t('projectDetail.procedureName')}
                                                />
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    handleSaveProcedureName(
                                                      extraction.id,
                                                      procedure.id,
                                                      editingProcedureName
                                                    )
                                                  }
                                                  className={`rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 active:scale-[0.98] ${MOTION}`}
                                                  title={t('common.save')}
                                                  aria-label={t('common.save')}
                                                >
                                                  <Check size={14} aria-hidden="true" />
                                                </button>
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    setEditingProcedureId(null);
                                                    setEditingProcedureName('');
                                                  }}
                                                  className={`rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 active:scale-[0.98] ${MOTION}`}
                                                  title={t('common.cancel')}
                                                  aria-label={t('common.cancel')}
                                                >
                                                  <X size={14} aria-hidden="true" />
                                                </button>
                                              </div>
                                            ) : (
                                              <div className="flex min-w-0 items-center gap-2">
                                                <span className="min-w-0 break-words hyphens-auto">
                                                  {procedure.procedure_name ?? procedure.assembly.assembly_id}
                                                </span>
                                                <button
                                                  type="button"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setEditingProcedureId(procedure.id);
                                                    setEditingProcedureName(
                                                      procedure.procedure_name ?? procedure.assembly.assembly_id ?? ''
                                                    );
                                                  }}
                                                  className={`rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 active:scale-[0.98] ${MOTION}`}
                                                  title={t('projectDetail.editProcedureName')}
                                                  aria-label={t('projectDetail.editProcedureName')}
                                                >
                                                  <PencilSimple size={14} aria-hidden="true" />
                                                </button>
                                              </div>
                                            )}
                                          </td>
                                          <td className={SUB_CELL_NUM}>
                                            {procedure.assembly.tool?.tool_number_unique}
                                          </td>
                                          <td className={SUB_CELL_NUM}>
                                            {procedure.assembly.tool?.tool_diameter.toFixed(2)}
                                          </td>
                                          <td className={SUB_CELL_NUM}>{procedure.total_depth.toFixed(2)}</td>
                                          <td className={`${SUB_CELL} break-words hyphens-auto`}>
                                            {procedure.mo_type}
                                          </td>
                                          <td className={SUB_CELL_NUM}>{procedure.spindle_speed_rpm.toFixed(0)}</td>
                                        </tr>
                                      ))}
                                </Fragment>
                              );
                            })}
                            {/* Empty rows to maintain fixed table height */}
                            {emptyRows.map((emptyRow) => (
                              <tr key={emptyRow.id} className={EMPTY_ROW}>
                                <td className={TABLE_CELL} />
                                <td className={TABLE_CELL} />
                                <td className={TABLE_CELL} />
                                <td className={TABLE_CELL} />
                                <td className={TABLE_CELL}>
                                  <span className="inline-block h-8 w-12" />
                                </td>
                                <td className={TABLE_CELL} />
                                <td className={`${TABLE_CELL} border-l border-gray-200`} colSpan={6} />
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div className={`${TABLE_FOOTER} flex-wrap gap-2`}>
                          <span className="whitespace-nowrap text-sm tabular-nums text-gray-600">
                            {t('common.showing') || 'Showing'} {startIndex + 1}–{Math.min(endIndex, sortedHoles.length)}{' '}
                            {t('common.of') || 'of'} {sortedHoles.length}
                          </span>
                          <div className="flex items-center gap-1">
                            <ActionButton
                              variant="ghost"
                              iconOnly={true}
                              icon={<CaretLeft size={16} aria-hidden="true" />}
                              onClick={() => handlePageChange(page - 1)}
                              disabled={page === 0}
                              aria-label={t('common.previousPage') || 'Previous page'}
                            />
                            {Array.from({ length: totalPages }, (_, i) => i).map((pageNum) => {
                              // Show first page, last page, current page, and pages around current
                              const showPage =
                                pageNum === 0 ||
                                pageNum === totalPages - 1 ||
                                (pageNum >= page - 1 && pageNum <= page + 1);

                              if (!showPage && pageNum === 1 && page > 2) {
                                return (
                                  <span key={pageNum} className="px-2 text-sm text-gray-400">
                                    …
                                  </span>
                                );
                              }
                              if (!showPage && pageNum === totalPages - 2 && page < totalPages - 3) {
                                return (
                                  <span key={pageNum} className="px-2 text-sm text-gray-400">
                                    …
                                  </span>
                                );
                              }
                              if (!showPage) return null;

                              return (
                                <button
                                  key={pageNum}
                                  type="button"
                                  onClick={() => handlePageChange(pageNum)}
                                  className={`${PAGE_BUTTON_BASE} ${pageNum === page ? PAGE_BUTTON_CURRENT : PAGE_BUTTON_IDLE}`}
                                  aria-label={`${t('common.page') || 'Page'} ${pageNum + 1}`}
                                  aria-current={pageNum === page ? 'page' : undefined}
                                >
                                  {pageNum + 1}
                                </button>
                              );
                            })}
                            <ActionButton
                              variant="ghost"
                              iconOnly={true}
                              icon={<CaretRight size={16} aria-hidden="true" />}
                              onClick={() => handlePageChange(page + 1)}
                              disabled={page >= totalPages - 1}
                              aria-label={t('common.nextPage') || 'Next page'}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </TabPanel>
              );
            })}
          </TabView>
        </div>
      )}

      {/* Preview Dialog – hole or procedure */}
      <ImagePreviewDialog
        visible={previewHoleId !== null || previewProcedureId !== null}
        title={
          previewHoleId !== null
            ? t('projectDetail.holePreview') || `Hole Preview ${previewHoleId}`
            : t('projectDetail.view3DPreview') || 'Zobrazit 3D náhled'
        }
        imageUrl={
          previewHoleId !== null
            ? previewImageUrl
            : previewProcedureId
              ? (previewProcedureThumbnails[previewProcedureId] ?? null)
              : null
        }
        error={
          (previewHoleId !== null && previewImageUrl === null) ||
          (previewProcedureId !== null && !previewProcedureThumbnails[previewProcedureId])
        }
        onClose={handleClosePreview}
      />

      {/* APT Source / H file Dialog */}
      {aptSourceContent && aptSourceHoleId && (
        <TextViewDialog
          visible={showAPTSourceDialog}
          title={aptSourceFilename ?? `Program: ${aptSourceHoleId}.h`}
          textContent={aptSourceContent}
          onClose={() => {
            setShowAPTSourceDialog(false);
            setAptSourceContent('');
            setAptSourceHoleId(null);
            setAptSourceFilename(null);
          }}
          downloadFilename={aptSourceFilename ?? `${aptSourceHoleId}.h`}
        />
      )}
    </div>
  );
};
